import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase, isSupabaseConfigured } from "../../services/supabase";
import { T } from "../../styles/GlobalStyles";
import Spin from "../../components/common/Spin";
import { track, EVENTS } from "../../services/analytics";
import { logger } from "../../utils/logger";
import { getReadableColor } from "../../utils/theme";

function formatRelative(iso, nowMs) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = nowMs - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `il y a ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 14) return `il y a ${days} j`;
  return d.toLocaleDateString("fr-FR");
}

function planColor(plan) {
  const colors = { free: T.muted, pro: T.accent, ultra: T.sectionFinisher };
  return colors[plan] || T.muted;
}

export default function AdminView() {
  const [nowMs] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [totals, setTotals] = useState({ coaches: 0, active7d: 0, clients: 0 });
  const [roleModal, setRoleModal] = useState(null);

  const load = useCallback(async () => {
    if (!supabase) return;
    const { data: coachesData, error } = await supabase
      .from("coaches")
      .select("id, email, first_name, last_name, plan, role, last_login_at, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) logger.warn("[admin] coaches load", { detail: error.message || error });
    const list = coachesData || [];
    setCoaches(list);

    /** Comptage explicite par coach (évite une agrégation globale ambiguë si RLS évolue). */
    const clientCounts = await Promise.all(
      list.map(async (coach) => {
        const { count } = await supabase
          .from("clients")
          .select("id", { count: "exact", head: true })
          .eq("coach_id", coach.id);
        return count ?? 0;
      })
    );
    const clientCount = clientCounts.reduce((a, b) => a + b, 0);

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const active7d = list.filter((c) => c.last_login_at && new Date(c.last_login_at) >= weekAgo).length;

    setTotals({
      coaches: list.length,
      active7d,
      clients: clientCount ?? 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    track(EVENTS.ADMIN_VIEW_OPENED, {});
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }
    void load();
  }, [load]);

  const updatePlan = async (id, plan) => {
    if (!supabase) return;
    const { error } = await supabase.from("coaches").update({ plan }).eq("id", id);
    if (!error) await load();
  };

  const confirmRole = async (id, role) => {
    if (!supabase) return;
    const { error } = await supabase.from("coaches").update({ role }).eq("id", id);
    if (!error) {
      setRoleModal(null);
      await load();
    }
  };

  if (!isSupabaseConfigured()) {
    return (
      <div style={{ padding: 24, color: T.muted, minHeight: "100vh", background: T.bg }}>
        <p style={{ marginBottom: 12 }}>Je charge le backoffice uniquement quand Supabase est configuré.</p>
        <Link to="/" style={{ color: T.accent, fontWeight: 700 }}>
          ← Retour à l’app
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: T.bg }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: 24 }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1
          className="condensed"
          style={{
            fontSize: 28,
            fontWeight: 800,
            background: `linear-gradient(90deg, ${T.sectionFinisher}, ${T.accent})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Backoffice AthleOS
        </h1>
        <Link to="/" style={{ color: T.accent, fontWeight: 700, textDecoration: "none" }}>
          ← Retour à l’app coach
        </Link>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 16,
          marginBottom: 32,
        }}
      >
        <Kpi label="Coachs inscrits" value={totals.coaches} />
        <Kpi label="Coachs actifs (7j)" value={totals.active7d} />
        <Kpi label="Clients total" value={totals.clients} />
      </div>

      <div style={{ overflowX: "auto", border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: T.surface, textAlign: "left" }}>
              <th style={{ padding: 12 }}>Nom</th>
              <th style={{ padding: 12 }}>Email</th>
              <th style={{ padding: 12 }}>Plan</th>
              <th style={{ padding: 12 }}>Rôle</th>
              <th style={{ padding: 12 }}>Dernière connexion</th>
              <th style={{ padding: 12 }}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {coaches.map((c) => {
              const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
              const daysSinceLogin = c.last_login_at
                ? (nowMs - new Date(c.last_login_at).getTime()) / 86400000
                : 999;
              const inactive = daysSinceLogin > 30;
              return (
                <tr key={c.id} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ padding: 12 }}>{name}</td>
                  <td style={{ padding: 12 }}>{c.email}</td>
                  <td style={{ padding: 12 }}>
                    <select
                      value={c.plan}
                      onChange={(e) => void updatePlan(c.id, e.target.value)}
                      style={{
                        background: `${planColor(c.plan)}18`,
                        color: planColor(c.plan),
                        border: `1px solid ${planColor(c.plan)}55`,
                        borderRadius: 8,
                        padding: "4px 8px",
                      }}
                    >
                      <option value="free">free</option>
                      <option value="pro">pro</option>
                      <option value="ultra">ultra</option>
                    </select>
                  </td>
                  <td style={{ padding: 12 }}>
                    <button
                      type="button"
                      onClick={() => setRoleModal({ id: c.id, email: c.email, role: c.role })}
                      style={{
                        background: T.surface3,
                        color: T.text,
                        border: `1px solid ${T.border}`,
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontWeight: 700,
                      }}
                    >
                      {c.role}
                    </button>
                  </td>
                  <td style={{ padding: 12, color: T.muted }}>{formatRelative(c.last_login_at, nowMs)}</td>
                  <td style={{ padding: 12 }}>{inactive ? "inactif" : "actif"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {roleModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 900,
            padding: 16,
          }}
        >
          <div style={{ background: T.surface, borderRadius: 12, padding: 24, maxWidth: 400, width: "100%" }}>
            <p style={{ marginBottom: 12, fontWeight: 700 }}>Changer le rôle</p>
            <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>{roleModal.email}</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => void confirmRole(roleModal.id, "coach")}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, color: T.text }}
              >
                coach
              </button>
              <button
                type="button"
                onClick={() => void confirmRole(roleModal.id, "admin")}
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  background: T.sectionFinisher,
                  color: getReadableColor(T.sectionFinisher),
                  fontWeight: 700,
                }}
              >
                admin
              </button>
            </div>
            <button
              type="button"
              onClick={() => setRoleModal(null)}
              style={{ marginTop: 12, width: "100%", padding: 10, color: T.muted }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ color: T.muted, fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800 }}>{value}</div>
    </div>
  );
}
